import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Building2,
  Users,
  ShieldCheck,
  MailPlus,
  Boxes,
  BarChart3,
  Kanban,
  LineChart,
  PieChart,
  Clock3,
  ChevronDown,
  ChevronRight,
  PanelLeftOpen,
  PanelLeftClose,
} from 'lucide-react';
import './Sidebar.css';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('sidebar-collapsed') !== 'false';
  });
  const [showCollapsedReports, setShowCollapsedReports] = useState(false);

  const { can } = usePermissions();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('sidebar-collapsed', isCollapsed ? 'true' : 'false');
  }, [isCollapsed]);

  useEffect(() => {
    setShowCollapsedReports(false);
  }, [location.pathname]);

  const menuItems = [
    {
      label: 'Empresas',
      path: '/companies',
      icon: Building2,
      permission: 'company.view',
    },
    {
      label: 'Usuários',
      path: '/company-users',
      icon: Users,
      permission: 'member.view',
    },
    {
      label: 'Perfis',
      path: '/roles',
      icon: ShieldCheck,
      permission: 'roles.view',
    },
    {
      label: 'Convites',
      path: '/invitations',
      icon: MailPlus,
      permission: 'invitations.view',
    },
  ];

  const rochaWmsItems = [
    {
      label: 'Espaços',
      path: '/rocha-wms/spaces',
      icon: Boxes,
      permission: 'areas.view',
    },
    {
      label: 'Relatórios',
      icon: BarChart3,
      permission: 'reports.view',
      hasSubmenu: true,
      submenuItems: [
        {
          label: 'Gráficos',
          path: '/rocha-wms/reports/charts',
          icon: LineChart,
          permission: 'reports.view',
        },
        {
          label: 'Cronogramas',
          path: '/rocha-wms/reports/schedules',
          icon: Kanban,
          permission: 'reports.view',
        },
        {
          label: 'Relatórios Gerais',
          path: '/rocha-wms/reports/time',
          icon: PieChart,
          permission: 'reports.view',
        },
        {
          label: 'Relatório de Horas',
          path: '/rocha-wms/reports/hours',
          icon: Clock3,
          permission: 'reports.view',
        },
      ],
    },
    {
      label: 'Kanban',
      path: '/rocha-wms/kanban',
      icon: Kanban,
      // Navegação do Kanban usa list.view/project.view
      permission: 'list.view',
    },
  ];

  const isActive = path => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleSubmenu = menuLabel => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuLabel]: !prev[menuLabel],
    }));
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(prev => !prev);
  };

  const handleSubmenuButtonClick = (event, item) => {
    if (isCollapsed) {
      event.preventDefault();
      event.stopPropagation();

      if (item.hasSubmenu && item.label === 'Relatórios') {
        setShowCollapsedReports(prev => !prev);
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log('[Sidebar] Exibindo popover de relatórios:', item.label);
      }
      return;
    }

    toggleSubmenu(item.label);
  };

  return (
    <>
      <aside
        className={`sidebar ${isOpen ? 'sidebar--open' : ''} ${isCollapsed ? 'sidebar--collapsed' : ''}`}
      >
        <div className="sidebar__toggle-wrapper">
          <button
            type="button"
            className="sidebar__toggle"
            onClick={handleToggleCollapse}
            aria-label={isCollapsed ? 'Exibir nomes do menu' : 'Compactar menu'}
          >
            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            <span className="sidebar__toggle-label">
              {isCollapsed ? 'Exibir nomes' : 'Compactar'}
            </span>
          </button>
        </div>

        <nav className="sidebar__nav">
          {/* Seção RochaWMS */}
          <div className="sidebar__section sidebar__section--top">
            <h3 className="sidebar__section-title">RochaWMS</h3>
            <ul className="sidebar__menu">
              {rochaWmsItems
                .filter(item => !item.permission || can(item.permission))
                .map(item => {
                  if (item.hasSubmenu && item.submenuItems) {
                    const isExpanded =
                      expandedMenus[item.label] !== undefined
                        ? expandedMenus[item.label]
                        : location.pathname.startsWith('/rocha-wms/reports');

                    const hasActiveSubmenu = item.submenuItems.some(subItem =>
                      isActive(subItem.path),
                    );

                    const isReportsItem = item.label === 'Relatórios';
                    const isReportsActive = item.submenuItems.some(subItem =>
                      isActive(subItem.path),
                    );
                    const isCollapsedOpen =
                      isCollapsed && isReportsItem && (showCollapsedReports || isReportsActive);

                    return (
                      <li
                        key={item.label}
                        className={`sidebar__item ${isCollapsedOpen ? 'sidebar__item--expanded' : ''} ${
                          isCollapsed && isReportsActive ? 'sidebar__item--active-collapsed' : ''
                        }`}
                      >
                        <button
                          onClick={event => handleSubmenuButtonClick(event, item)}
                          className={`sidebar__link ${hasActiveSubmenu ? 'sidebar__link--active' : ''}`}
                          style={{ width: '100%', textAlign: 'left' }}
                          title={isCollapsed ? item.label : undefined}
                          aria-label={item.label}
                          type="button"
                        >
                          <span className="sidebar__icon">
                            {item.icon ? <item.icon size={20} strokeWidth={1.8} /> : null}
                          </span>
                          <span className="sidebar__label">{item.label}</span>
                          {!isCollapsed ? (
                            isExpanded ? (
                              <ChevronDown className="h-4 w-4 ml-auto" />
                            ) : (
                              <ChevronRight className="h-4 w-4 ml-auto" />
                            )
                          ) : isReportsItem ? (
                            <span className="sidebar__collapsed-indicator">
                              {isCollapsedOpen ? '–' : '+'}
                            </span>
                          ) : null}
                        </button>
                        {isExpanded && !isCollapsed && (
                          <ul className="sidebar__submenu">
                            {item.submenuItems
                              .filter(subItem => !subItem.permission || can(subItem.permission))
                              .map(subItem => (
                                <li key={subItem.path} className="sidebar__submenu-item">
                                  <Link
                                    to={subItem.path}
                                    className={`sidebar__submenu-link ${isActive(subItem.path) ? 'sidebar__submenu-link--active' : ''}`}
                                    title={isCollapsed ? subItem.label : undefined}
                                    onClick={onClose}
                                  >
                                    <span className="sidebar__submenu-icon">
                                      {subItem.icon ? (
                                        <subItem.icon size={16} strokeWidth={1.6} />
                                      ) : (
                                        <span className="sidebar__submenu-bullet" />
                                      )}
                                    </span>
                                    <span className="sidebar__submenu-label">{subItem.label}</span>
                                  </Link>
                                </li>
                              ))}
                          </ul>
                        )}
                        {isCollapsedOpen && (
                          <ul className="sidebar-collapsed-submenu">
                            {item.submenuItems.map(subItem => (
                              <li
                                key={subItem.path}
                                className={`sidebar-collapsed-submenu__item ${
                                  isActive(subItem.path)
                                    ? 'sidebar-collapsed-submenu__item--active'
                                    : ''
                                }`}
                              >
                                <Link
                                  to={subItem.path}
                                  className="sidebar-collapsed-submenu__link"
                                  onClick={onClose}
                                  title={subItem.label}
                                  aria-label={subItem.label}
                                >
                                  {subItem.icon ? (
                                    <span className="sidebar-collapsed-submenu__icon">
                                      <subItem.icon size={18} strokeWidth={1.6} />
                                    </span>
                                  ) : null}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  }

                  return (
                    <li key={item.path} className="sidebar__item">
                      <Link
                        to={item.path}
                        className={`sidebar__link ${isActive(item.path) ? 'sidebar__link--active' : ''}`}
                        title={isCollapsed ? item.label : undefined}
                        aria-label={item.label}
                        onClick={onClose}
                      >
                        <span className="sidebar__icon">
                          {item.icon ? <item.icon size={20} strokeWidth={1.8} /> : null}
                        </span>
                        <span className="sidebar__label">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
            </ul>
          </div>

          <ul className="sidebar__menu">
            {menuItems
              .filter(item => !item.permission || can(item.permission))
              .map(item => (
                <li key={item.path} className="sidebar__item">
                  <Link
                    to={item.path}
                    className={`sidebar__link ${isActive(item.path) ? 'sidebar__link--active' : ''}`}
                    title={isCollapsed ? item.label : undefined}
                    aria-label={item.label}
                    onClick={onClose}
                  >
                    <span className="sidebar__icon">
                      {item.icon ? <item.icon size={20} strokeWidth={1.8} /> : null}
                    </span>
                    <span className="sidebar__label">{item.label}</span>
                  </Link>
                </li>
              ))}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
